import { useEffect, useState } from "react";
import { Plus, Upload, Edit, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const STATUS_PILLS = {
  placed: "bg-jow-navy/10 text-jow-navy",
  packed: "bg-jow-gold/20 text-jow-navy",
  shipped: "bg-blue-50 text-blue-700",
  on_rikshaw: "bg-jow-gold/30 text-jow-navy",
  out_for_delivery: "bg-jow-pink/20 text-jow-pink",
  delivered: "bg-green-50 text-green-700",
  cancelled: "bg-red-50 text-red-700",
  reserved: "bg-purple-50 text-purple-700",
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = new URLSearchParams({ page, limit: 20 });
    if (q) params.set("q", q);
    if (filter === "active") params.set("is_active", "true");
    if (filter === "inactive") params.set("is_active", "false");
    if (filter === "low_stock") params.set("low_stock", "true");
    const { data } = await api.get(`/admin/products?${params.toString()}`);
    setItems(data.items || []);
    setTotal(data.total || 0);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, q, filter]);

  const toggleActive = async (p) => {
    await api.patch(`/admin/products/${p.id}`, { is_active: !p.is_active });
    toast.success(`${p.name} ${!p.is_active ? "activated" : "deactivated"}`);
    load();
  };

  const onSaved = () => {
    setDrawerOpen(false);
    setEditing(null);
    load();
  };

  return (
    <div className="space-y-5" data-testid="admin-products">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="label-overline text-jow-gold">Catalog</span>
          <h1 className="font-display text-3xl text-jow-navy mt-1">Products</h1>
          <p className="text-xs text-jow-navy/55 mt-1">{total} total</p>
        </div>
        <div className="flex items-center gap-2">
          <BulkImport onDone={load} />
          <button
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
            className="btn-primary-jow text-[11px]"
            data-testid="new-product-btn"
          >
            <Plus size={13} /> New product
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="Search name / slug / material…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="flex-1 min-w-[240px] border border-jow-navy/15 px-3 py-2 text-sm bg-white outline-none focus:border-jow-gold"
          data-testid="prod-search"
        />
        <select
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(1);
          }}
          className="border border-jow-navy/15 px-3 py-2 text-sm bg-white"
          data-testid="prod-filter"
        >
          <option value="">All products</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive</option>
          <option value="low_stock">Low stock (≤5)</option>
        </select>
      </div>

      <table className="w-full bg-white border border-jow-navy/10 text-sm">
        <thead className="bg-jow-navy/5 text-jow-navy/70 uppercase tracking-widest text-[10px]">
          <tr>
            <th className="text-left px-4 py-3">Product</th>
            <th className="text-left px-4 py-3">Category</th>
            <th className="text-right px-4 py-3">Price</th>
            <th className="text-right px-4 py-3">Warehouse</th>
            <th className="text-center px-4 py-3">Active</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => {
            const wh = (p.variants || []).reduce((a, v) => a + (v.stock_warehouse || 0), 0);
            return (
              <tr
                key={p.id}
                className="border-t border-jow-navy/5"
                data-testid={`prow-${p.slug}`}
              >
                <td className="px-4 py-3 flex items-center gap-3">
                  {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 object-cover" />}
                  <div>
                    <p className="text-jow-navy">{p.name}</p>
                    <p className="text-[10px] text-jow-navy/50">{p.slug}</p>
                  </div>
                </td>
                <td className="px-4 py-3 text-jow-navy/70 capitalize">
                  {p.category_slug}
                </td>
                <td className="px-4 py-3 text-right font-display">
                  ₹{p.price_inr.toLocaleString("en-IN")}
                </td>
                <td
                  className={`px-4 py-3 text-right ${wh <= 5 ? "text-jow-pink font-bold" : "text-jow-navy"}`}
                  data-testid={`prow-wh-${p.slug}`}
                >
                  {wh}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => toggleActive(p)}
                    className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                      p.is_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                    }`}
                    data-testid={`prow-toggle-${p.slug}`}
                  >
                    {p.is_active ? "on" : "off"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => {
                      setEditing(p);
                      setDrawerOpen(true);
                    }}
                    className="text-jow-pink"
                    data-testid={`prow-edit-${p.slug}`}
                  >
                    <Edit size={13} />
                  </button>
                </td>
              </tr>
            );
          })}
          {items.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center py-8 text-jow-navy/50">
                No products match.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex items-center justify-between text-xs text-jow-navy/55">
        <span>
          Page {page} of {Math.max(1, Math.ceil(total / 20))}
        </span>
        <div className="flex gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="btn-secondary-jow text-[10px] disabled:opacity-30"
            data-testid="prev-page"
          >
            Prev
          </button>
          <button
            disabled={page * 20 >= total}
            onClick={() => setPage((p) => p + 1)}
            className="btn-secondary-jow text-[10px] disabled:opacity-30"
            data-testid="next-page"
          >
            Next
          </button>
        </div>
      </div>

      {drawerOpen && (
        <ProductDrawer
          editing={editing}
          onClose={() => {
            setDrawerOpen(false);
            setEditing(null);
          }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function ProductDrawer({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(
    editing || {
      slug: "",
      name: "",
      description: "",
      category_slug: "earrings",
      price_inr: 999,
      mrp_inr: 1299,
      images: ["", "", ""],
      variants: [{ sku: "", color: "Gold", size: "Free Size", stock_warehouse: 10 }],
      material: "",
      occasion_tags: [],
      is_active: true,
    }
  );
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, images: form.images.filter(Boolean) };
      if (editing) {
        await api.patch(`/admin/products/${editing.id}`, body);
        toast.success("Product updated.");
      } else {
        await api.post("/admin/products", body);
        toast.success("Product created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save.");
    } finally {
      setBusy(false);
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVariant = (i, k, v) => {
    const n = [...form.variants];
    n[i] = { ...n[i], [k]: v };
    setForm((f) => ({ ...f, variants: n }));
  };
  const addVariant = () =>
    setForm((f) => ({
      ...f,
      variants: [...f.variants, { sku: "", color: "Gold", size: "Free Size", stock_warehouse: 0 }],
    }));
  const delVariant = (i) =>
    setForm((f) => ({ ...f, variants: f.variants.filter((_, k) => k !== i) }));

  return (
    <div
      className="fixed inset-0 z-40 bg-jow-navy/50 flex justify-end"
      onClick={onClose}
      data-testid="product-drawer"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white h-full overflow-y-auto p-6 space-y-5"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl text-jow-navy">
            {editing ? "Edit product" : "New product"}
          </h2>
          <button onClick={onClose} className="text-jow-navy/50">
            <X size={18} />
          </button>
        </div>
        {!editing && (
          <Input
            label="Slug"
            value={form.slug}
            onChange={(v) => setField("slug", v)}
            testid="pd-slug"
          />
        )}
        <Input label="Name" value={form.name} onChange={(v) => setField("name", v)} testid="pd-name" />
        <Textarea
          label="Description"
          value={form.description || ""}
          onChange={(v) => setField("description", v)}
          testid="pd-desc"
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Category slug"
            value={form.category_slug}
            onChange={(v) => setField("category_slug", v)}
            testid="pd-cat"
          />
          <Input
            label="Material"
            value={form.material || ""}
            onChange={(v) => setField("material", v)}
            testid="pd-material"
          />
          <Input
            label="Price (₹)"
            type="number"
            value={form.price_inr}
            onChange={(v) => setField("price_inr", Number(v))}
            testid="pd-price"
          />
          <Input
            label="MRP (₹)"
            type="number"
            value={form.mrp_inr}
            onChange={(v) => setField("mrp_inr", Number(v))}
            testid="pd-mrp"
          />
        </div>
        <div>
          <label className="label-overline block mb-1">Image URLs (up to 3)</label>
          {form.images.map((u, i) => (
            <input
              key={i}
              type="url"
              value={u}
              placeholder={`Image ${i + 1}`}
              onChange={(e) => {
                const n = [...form.images];
                n[i] = e.target.value;
                setField("images", n);
              }}
              className="w-full border border-jow-navy/15 px-3 py-2 text-sm mt-1"
              data-testid={`pd-image-${i}`}
            />
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="label-overline">Variants</label>
            <button
              onClick={addVariant}
              className="text-[11px] text-jow-pink uppercase tracking-widest"
              data-testid="pd-add-variant"
            >
              + variant
            </button>
          </div>
          <div className="space-y-2">
            {form.variants.map((v, i) => (
              <div key={i} className="grid grid-cols-5 gap-2" data-testid={`pd-variant-${i}`}>
                <input
                  value={v.sku}
                  placeholder="SKU"
                  onChange={(e) => setVariant(i, "sku", e.target.value)}
                  className="col-span-2 border border-jow-navy/15 px-2 py-1 text-xs"
                />
                <input
                  value={v.color || ""}
                  placeholder="Color"
                  onChange={(e) => setVariant(i, "color", e.target.value)}
                  className="border border-jow-navy/15 px-2 py-1 text-xs"
                />
                <input
                  value={v.size || ""}
                  placeholder="Size"
                  onChange={(e) => setVariant(i, "size", e.target.value)}
                  className="border border-jow-navy/15 px-2 py-1 text-xs"
                />
                <div className="flex">
                  <input
                    type="number"
                    value={v.stock_warehouse}
                    onChange={(e) => setVariant(i, "stock_warehouse", Number(e.target.value))}
                    className="flex-1 border border-jow-navy/15 px-2 py-1 text-xs"
                  />
                  <button
                    onClick={() => delVariant(i)}
                    className="px-2 text-jow-pink"
                    data-testid={`pd-del-variant-${i}`}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={save}
          disabled={busy}
          className="btn-primary-jow w-full"
          data-testid="pd-save"
        >
          {busy ? "Saving…" : editing ? "Save changes" : "Create product"}
        </button>
      </aside>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-jow-navy/15 px-3 py-2 text-sm bg-white"
        data-testid={testid}
      />
    </div>
  );
}

function Textarea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full border border-jow-navy/15 px-3 py-2 text-sm bg-white"
        data-testid={testid}
      />
    </div>
  );
}

function BulkImport({ onDone }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported · created ${data.created}, updated ${data.updated}`);
      if (data.errors?.length) toast.error(`${data.errors.length} row errors`);
      onDone();
    } catch (err) {
      toast.error("Bulk import failed.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <label
      className="btn-secondary-jow text-[11px] cursor-pointer"
      data-testid="bulk-import-label"
    >
      <Upload size={13} /> {busy ? "Importing…" : "Bulk CSV"}
      <input type="file" accept=".csv" onChange={upload} hidden data-testid="bulk-import-input" />
    </label>
  );
}
